import { lazy, Suspense, useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";

import { openIntake } from "./config/intake";
import { captureLeadParams, submitLead, trackLeadEvent } from "./lib/leadCapture.js";
import { useBotDefense, HoneypotField } from "./lib/botDefense.jsx";
import { HOME_FAQS as HOME_FAQ_DATA } from "./lib/homeFaqs.js";
import { getFeaturedArticles } from "./lib/learnArticles.js";
import { PRICING, PRICE_COPY } from "./lib/pricing.js";
import lifestylePhone480 from "@assets/optimized/lifestyle-phone-480.webp";
import lifestylePhone768 from "@assets/optimized/lifestyle-phone-768.webp";
import lifestylePackage720 from "@assets/optimized/lifestyle-package-720.webp";
import lifestyleWalk750 from "@assets/optimized/lifestyle-walk-750.webp";
import lifestyleWalk1200 from "@assets/optimized/lifestyle-walk-1200.webp";
import step2Img480 from "@assets/optimized/step-2-480.webp";
import step2Img768 from "@assets/optimized/step-2-768.webp";
import vyvWeightLoss from "@assets/vyv-weightloss_1790052887693.webp";
import vyvLongevity from "@assets/vyv-longevity_1790052887692.webp";
import vyvMicrodose from "@assets/vyv-microdose_1790136558757.webp";
import sceneClinical from "@assets/scene-clinical_1790012227584.webp";

const LeadCaptureModal = lazy(() => import("./components/LeadCaptureModal.jsx"));

// Internal-only flag. Hides pre-launch placeholder slots
// (trust logo grid). Set true again only when finalized assets are ready.
const SHOW_PLACEHOLDER_SLOTS = false;

// TODO: Replace with final OpenLoop state availability language after backend approval. Confirm Mississippi availability before launch.

const C = {
  navy: "#0A1628",
  navy2: "#1E2C40",
  navy3: "#2D4160",
  navy4: "#394F70",
  navyTint: "#7D96BC",
  navyHover: "#162240",
  blue: "#237FD3",
  primaryBlue: "#1C6AB4",
  teal: "#0E9384",
  champagne: "#FAF7F2",
  bg: "#f0f4f4",
  lightBg: "#F3F7F6",
  muted: "#5B6470",
  footerMuted: "#B8C2D1",
  border: "#E5E7EB",
  white: "#FFFFFF",
  text: "#2C2C2C",
};

const DISCLAIMERS = {
  cta: "Provider review required. Prescription treatment is only available if clinically appropriate.",
  glp1: "GLP-1 medications may not be appropriate for everyone. A licensed provider will review your medical history, eligibility, and treatment options before any prescription decision is made.",
  long: "Vyvron is not a medical provider, pharmacy, or prescription fulfillment service. Vyvron connects users with independent licensed medical providers who determine whether treatment is clinically appropriate. Prescription treatment is not guaranteed. Medication availability, pricing, and fulfillment may vary by patient eligibility, provider judgment, pharmacy availability, and state law.",
  state: "Vyvron services and affiliated provider availability may vary by state. Some states may be excluded based on clinical partner availability, regulatory requirements, or pharmacy fulfillment limitations. Vyvron is not currently available in all states.",
};

const font = (size, weight = 400, color = C.text, extra = {}) => ({
  fontFamily: "'Inter', sans-serif", fontSize: size, fontWeight: weight, color, lineHeight: 1.7, ...extra,
});

const serif = (size, weight = 800, color = C.navy, extra = {}) => ({
  fontFamily: "'DM Sans', sans-serif", fontSize: size, fontWeight: weight, color, lineHeight: 1.2, letterSpacing: "-0.5px", ...extra,
});

// Single-color line icons for the How It Works tiles — one accent tint only.
const howIcon = (paths) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#237FD3" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    {paths}
  </svg>
);
const HOW = [
  { n: "01", icon: howIcon(<><rect x="5" y="3" width="14" height="18" rx="2" /><path d="M9 8h6M9 12h6M9 16h4" /></>), title: "Answer a few questions.", body: "About 60 seconds, private, no commitment.", image: lifestylePhone480, imageSrcSet: `${lifestylePhone480} 480w, ${lifestylePhone768} 768w`, imageWidth: 480, imageHeight: 720, imageAlt: "Woman completing a private health questionnaire on her phone", imagePos: "top center" },
  { n: "02", icon: howIcon(<><path d="M5 3v6a5 5 0 0 0 10 0V3" /><path d="M10 14v2a5 5 0 0 0 10 0v-1" /><circle cx="20" cy="12" r="2" /></>), title: "Check out — you're charged upfront.", body: "Then a licensed provider reviews everything you submit.", image: step2Img480, imageSrcSet: `${step2Img480} 480w, ${step2Img768} 768w`, imageWidth: 480, imageHeight: 720, imageAlt: "Licensed clinician reviewing patient information", imagePos: "center" },
  { n: "03", icon: howIcon(<><rect x="3" y="9" width="18" height="6" rx="3" transform="rotate(-45 12 12)" /><path d="M8.5 15.5l7-7" /></>), title: "Approved? Your medication ships from a licensed US pharmacy.", body: "Not approved? You receive a full refund — you never pay for a treatment you can't get.", image: lifestylePackage720, imageWidth: 720, imageHeight: 720, imageAlt: "Discreet Vyvron treatment package in a home setting", imagePos: "center" },
];

function NavBar({ scrolled }) {
  const handleCTA = openIntake;
  const [mobileOpen, setMobileOpen] = useState(false);
  const toggleRef = useRef(null);
  const navLinks = [
    { label: "How It Works", id: "how-it-works" },
    { label: "Programs", href: "/programs" },
    { label: "Pricing", id: "pricing" },
    { label: "Learn", href: "/learn" },
    { label: "Patient Login", href: "https://start.vyvron.com/login", mobileOnly: true },
  ];
  const closeMenu = () => {
    setMobileOpen(false);
    setTimeout(() => toggleRef.current?.focus(), 0);
  };
  const goToSection = (id) => {
    closeMenu();
    setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 60);
  };
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e) => { if (e.key === "Escape") closeMenu(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileOpen]);

  return (
    <>
      <nav className={`vyv-home-nav${scrolled ? " is-scrolled" : ""}`} style={{
        position: "sticky", top: 0, left: 0, right: 0, zIndex: 1000,
        background: "rgba(250,247,242,0.88)",
        borderBottom: "1px solid rgba(10,22,40,0.08)",
        backdropFilter: "blur(18px)",
        WebkitBackdropFilter: "blur(18px)",
        transition: "height 0.25s ease, box-shadow 0.25s ease, background 0.25s ease",
        boxShadow: scrolled ? "0 8px 28px rgba(10,22,40,0.08)" : "none",
        padding: "0 48px", height: scrolled ? 62 : 72,
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} style={{ display: "flex", alignItems: "center", gap: 0, cursor: "pointer" }}>
          <span style={font(22, 800, C.navy, { letterSpacing: "-0.5px", lineHeight: 1 })}>
            Vyvron
          </span>
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginLeft: 3, marginBottom: 1 }}>
            <rect x="7" y="0" width="4" height="18" rx="1.5" fill={C.blue} />
            <rect x="0" y="7" width="18" height="4" rx="1.5" fill={C.blue} />
          </svg>
        </div>
        <div className="vyv-nav-links" style={{ display: "flex", gap: 36 }}>
          {navLinks.filter(l => !l.mobileOnly).map(({ label, id, href }) => (
            href ? (
              <a key={label} href={href} style={{ ...font(14, 500, C.muted), cursor: "pointer", textDecoration: "none" }}
                onMouseEnter={e => e.target.style.color = C.navy}
                onMouseLeave={e => e.target.style.color = C.muted}>
                {label}
              </a>
            ) : (
              <span key={label} style={{ ...font(14, 500, C.muted), cursor: "pointer" }}
                onClick={() => goToSection(id)}
                onMouseEnter={e => e.target.style.color = C.navy}
                onMouseLeave={e => e.target.style.color = C.muted}>
                {label}
              </span>
            )
          ))}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <a href="https://start.vyvron.com/login" className="vyv-nav-login vyv-nav-login--home" style={{
            background: "transparent", color: C.navy,
            border: `1px solid ${C.navy}`, borderRadius: 12,
            padding: "11px 20px", textDecoration: "none",
            fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600,
            display: "inline-flex", alignItems: "center", minHeight: 44,
            transition: "color 0.2s, border-color 0.2s",
          }}
            onMouseEnter={e => { e.currentTarget.style.color = "#0A1628"; e.currentTarget.style.borderColor = "#237FD3"; }}
            onMouseLeave={e => { e.currentTarget.style.color = C.navy; e.currentTarget.style.borderColor = C.navy; }}>
            Patient Login
          </a>
          <button onClick={handleCTA} className="vyv-nav-cta" style={{
            background: C.primaryBlue, color: C.white, border: "none",
            borderRadius: 6, padding: "12px 26px",
            ...font(14, 500, C.white, { letterSpacing: "0.5px" }),
            cursor: "pointer", transition: "background 0.2s",
            minHeight: 44,
          }}
            onMouseEnter={e => e.target.style.background = C.navy}
            onMouseLeave={e => e.target.style.background = C.primaryBlue}>
            See if you qualify
          </button>
          <button
            ref={toggleRef}
            className="vyv-mobile-toggle"
            onClick={() => setMobileOpen(o => !o)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            aria-controls="vyv-mobile-menu"
            style={{
              display: "none",
              background: "transparent", border: "none",
              padding: 10, cursor: "pointer",
              minWidth: 44, minHeight: 44,
              alignItems: "center", justifyContent: "center",
            }}>
            <svg width="22" height="22" viewBox="0 0 24 24" stroke={C.navy} strokeWidth="2" fill="none" strokeLinecap="round">
              {mobileOpen ? (
                <>
                  <line x1="6" y1="6" x2="18" y2="18" />
                  <line x1="6" y1="18" x2="18" y2="6" />
                </>
              ) : (
                <>
                  <line x1="3" y1="7" x2="21" y2="7" />
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="17" x2="21" y2="17" />
                </>
              )}
            </svg>
          </button>
        </div>
      </nav>
      {mobileOpen && (
        <div id="vyv-mobile-menu" role="menu" className="vyv-mobile-menu" style={{
          position: "fixed", top: 64, left: 0, right: 0, zIndex: 999,
          background: C.white, borderBottom: `1px solid ${C.border}`,
          boxShadow: "0 12px 28px rgba(10,22,40,0.10)",
          padding: "8px 0",
        }}>
          {navLinks.map(({ label, id, href }) => (
            <button key={label} role="menuitem" onClick={() => href ? (window.location.href = href) : goToSection(id)} style={{
              display: "block", width: "100%", textAlign: "left",
              background: "transparent", border: "none", cursor: "pointer",
              padding: "14px 24px", minHeight: 48,
              borderTop: `1px solid ${C.border}`,
              ...font(15, 500, C.navy, { letterSpacing: "0.2px" }),
            }}>
              {label}
            </button>
          ))}
        </div>
      )}
    </>
  );
}

function Ticker() {
  const items = [
    "Licensed US Pharmacies",
    "100% Online",
    "Independent Licensed Providers",
    "Discreet Shipping",
  ];
  return (
    <div className="vyv-home-ticker" style={{ background: C.navy, minHeight: 40, display: "flex", alignItems: "center", justifyContent: "center", flexWrap: "wrap", gap: 14, padding: "8px 20px", marginTop: 0 }}>
      {items.map((t, i) => (
        <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 14 }}>
          {i > 0 && (
            <span aria-hidden style={{ width: 5, height: 5, borderRadius: "50%", background: "#237FD3", display: "inline-block" }}></span>
          )}
          <span style={font(12, 600, "rgba(255,255,255,0.85)", { letterSpacing: "0.12em", textTransform: "uppercase" })}>{t}</span>
        </span>
      ))}
    </div>
  );
}

/* MWL limited-time offer. */
function FoundingBanner() {
  const btn = {
    display: "inline-block",
    background: C.primaryBlue,
    color: "#FFFFFF",
    borderRadius: 6,
    padding: "13px 22px",
    fontFamily: "'DM Sans',sans-serif",
    fontWeight: 700,
    fontSize: 15,
    textDecoration: "none",
    textAlign: "center",
  };
  return (
    <section aria-label="Medical Weight Loss limited-time offer" style={{ background: "#FAF7F2", borderBottom: "1px solid rgba(10,22,40,0.08)", padding: "56px 24px" }}>
      <div style={{ maxWidth: 820, margin: "0 auto", textAlign: "center" }}>
        <p style={{ display: "inline-flex", background: "rgba(35,127,211,0.10)", border: "1px solid rgba(35,127,211,0.24)", borderRadius: 999, padding: "6px 12px", fontFamily: "'DM Sans',sans-serif", fontWeight: 800, fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: "#0A1628", margin: "0 0 14px" }}>
          Limited-Time Offer
        </p>
        <h2 style={{ fontFamily: "'DM Sans',sans-serif", fontWeight: 700, fontSize: 32, lineHeight: 1.2, color: "#0A1628", margin: "0 0 14px" }}>
          Medical Weight Loss — limited-time pricing.
        </h2>
        <p style={{ fontFamily: "'Inter',sans-serif", fontSize: 14, fontWeight: 600, color: "#0A1628", lineHeight: 1.55, margin: "0 auto 14px" }}>
          {PRICE_COPY.medicalWeightLossPricingLine}
        </p>
        <p style={{ fontFamily: "'Inter',sans-serif", fontSize: 16, color: "#4A5568", lineHeight: 1.7, maxWidth: 680, margin: "0 auto 24px" }}>
          {`${PRICE_COPY.medicalWeightLossPricingLine} The price you see is the price you pay, no hidden fees.`}
        </p>
        <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap", marginBottom: 12 }}>
          <a href="/start" style={btn}>See if you qualify</a>
        </div>
        <p style={{ fontFamily: "'Inter',sans-serif", fontSize: 12.5, color: "#5B6470", lineHeight: 1.6, maxWidth: 680, margin: "0 auto 16px" }}>
          {PRICE_COPY.roscaBillingLine("medicalWeightLoss")}
        </p>
        <p style={{ fontFamily: "'DM Sans',sans-serif", fontWeight: 600, fontSize: 13.5, color: "#5B6470", margin: "0 0 18px" }}>
          Also available:{" "}
          <Link to="/microdose" style={{ color: "#0A1628" }}>Microdose</Link>
          {" · "}
          <Link to="/longevity" style={{ color: "#0A1628" }}>Longevity</Link>
        </p>
        <p className="vyv-disclosure-text vyv-disclosure-text--light" style={{ fontFamily: "'Inter',sans-serif", fontSize: 12, color: "#5B6470", lineHeight: 1.65, maxWidth: 680, margin: "0 auto" }}>
          Where compounded medications are offered: Compounded medications are not FDA-approved, and are not available to Mississippi residents. Provider review required; treatment is not guaranteed. Vyvron is not a medical provider or pharmacy.
        </p>
      </div>
    </section>
  );
}

// Starts the /start qualifying quiz from the homepage: the intent flag is the
// same one the /start static hero sets — Start.jsx consumes it on mount and
// jumps straight into quiz step 1 (no scroll, no hero stop).
function startQuiz(navigate) {
  try { sessionStorage.setItem("vy_start_cta_intent", "1"); } catch { /* private mode */ }
  navigate("/start");
}

function Hero() {
  const navigate = useNavigate();
  const receiptItems = [
    ["Private provider evaluation", "Included"],
    ["Medication, if prescribed", "Included"],
    ["Ongoing provider access", "Included"],
    ["Hidden fees", "$0"],
    ["Surprise renewals", "$0"],
  ];

  return (
    <section className="vyv-hero" style={{
      background: C.champagne,
      padding: "92px 48px 86px",
      position: "relative",
    }}>
      <div className="vyv-hero-layout" style={{ maxWidth: 1200, margin: "0 auto", position: "relative", display: "grid", gridTemplateColumns: "minmax(0, 0.94fr) minmax(440px, 1.06fr)", gap: 64, alignItems: "center" }}>
        <div className="vyv-hero-copy">
          <p className="vyv-hero-eyebrow">
            Telehealth, run clean
          </p>

          <h1>
            No markups. No mystery fees. Just <span>care.</span>
          </h1>

          <p className="vyv-hero-lede">
            Weight loss, microdosing, and longevity — with independent licensed US providers and a price you can read before you start.
          </p>

          <div className="vyv-hero-mini-price" aria-label="Medical Weight Loss limited-time price">
            <strong>{PRICING.medicalWeightLoss.founding.display}</strong>
            <s>{PRICING.medicalWeightLoss.standard.display}</s>
            <span>/ 4 wks · limited-time</span>
            <span>· $0 hidden fees</span>
          </div>

          <div className="vyv-hero-actions">
            <button onClick={() => startQuiz(navigate)} className="vyv-btn-primary">
              See if you qualify
            </button>
            <a href="#programs">Explore programs <span aria-hidden="true">→</span></a>
          </div>

          <ul className="vyv-hero-audit">
            {[
              "LegitScript Certified — independently verifiable",
              "Independent licensed US providers",
              "Provider review required — not everyone qualifies",
            ].map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>

        <div className="vyv-hero-visual">
          <div className="vyv-hero-product">
            <img
              src={vyvWeightLoss}
              alt="Weight-loss program: capped injector pen, sealed vial and tablet"
              loading="eager"
              fetchpriority="high"
              decoding="async"
            />
          </div>

          <div className="vyv-modern-card vyv-hero-receipt">
            <div className="vyv-receipt-meta">
              <span>Medical Weight Loss</span>
              <span>Limited-time offer</span>
            </div>
            <h2>Everything in. Nothing hidden.</h2>
            <div className="vyv-receipt-lines">
              {receiptItems.map(([label, value]) => (
                <div key={label}>
                  <span>{label}</span>
                  <i aria-hidden="true"></i>
                  <strong>{value}</strong>
                </div>
              ))}
            </div>
            <div className="vyv-receipt-total">
              <div>
                <strong>Your price</strong>
                <span>Regularly {PRICING.medicalWeightLoss.standard.display}</span>
              </div>
              <div className="vyv-receipt-price">
                <strong>{PRICING.medicalWeightLoss.founding.display}</strong>
                <s>{PRICING.medicalWeightLoss.standard.display}</s>
                <span>/ 4 wks</span>
              </div>
            </div>
            <p className="vyv-receipt-footnote">
              {PRICE_COPY.medicalWeightLossPricingLine} You're charged upfront, then a provider reviews your plan. If you're denied, you receive a full refund. Compounded medications are not FDA-approved.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function NoGamesStrip() {
  return (
    <section className="vyv-no-games-strip" aria-label="Clear program terms">
      <div>
        {[
          "$0 hidden fees",
          "Licensed US providers",
          "LegitScript-certified",
          "Clear refill terms",
        ].map((item) => (
          <span key={item}><i aria-hidden="true">✓</i>{item}</span>
        ))}
      </div>
    </section>
  );
}

// LegitScript trust strip — navy band directly under the hero.
function TrustStrip() {
  const pill = (label, extra = {}) => (
    <span style={{ fontFamily: "Inter,sans-serif", fontSize: 12, fontWeight: 600, color: "#CBD5E1", letterSpacing: "0.04em", ...extra }}>{label}</span>
  );
  const dot = <span aria-hidden="true" style={{ color: "rgba(255,255,255,0.2)" }}>·</span>;
  return (
    <section id="vyv-trust-strip" aria-label="Third-party verification" style={{
      background: "#0A1628",
      borderTop: "1px solid rgba(35,127,211,0.18)",
      borderBottom: "1px solid rgba(35,127,211,0.18)",
      padding: "18px 48px",
    }}>
      <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "center", flexWrap: "wrap", gap: "12px 28px" }}>
        <a
          href="https://www.legitscript.com/websites/?checker_keywords=vyvron.com"
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: "inline-flex", alignItems: "center", height: 40 }}
        >
          <img
            src="https://static.legitscript.com/seals/50305996.png"
            alt="LegitScript Certified — verify"
            loading="lazy"
            decoding="async"
            style={{ display: "block", height: 40, width: "auto", objectFit: "contain" }}
          />
        </a>
        {dot}
        {pill("Independent Licensed US Providers")}
        {dot}
        {pill("Licensed US Pharmacy Partners")}
        {dot}
        {pill("The price you see is the price you pay")}
      </div>
    </section>
  );
}

// Single-color accent line icons for the benefit strip below the hero.
const stripIcon = (paths) => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#237FD3" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    {paths}
  </svg>
);

function SupportSection() {
  const navigate = useNavigate();
  return (
    <section id="vyv-social-proof" aria-label="Our honesty standard" style={{ background: C.champagne, padding: "110px 48px" }}>
      <div style={{ maxWidth: 860, margin: "0 auto", textAlign: "center" }}>
        <h2 style={serif(40, 700, C.navy, { letterSpacing: "-0.5px", lineHeight: 1.15, marginBottom: 24 })}>
          Half the ads you'll see this week are hoping you don't read the fine print.
        </h2>
        <p style={font(17, 400, C.muted, { lineHeight: 1.75, maxWidth: 720, margin: "0 auto 48px" })}>
          {'So here\'s ours — all of it. No "starting at" games. No surprise renewals. No 20-minute upsell call. One price you can see before you commit, real providers making real decisions, and you can cancel by email whenever you want.'}
        </p>
        <p style={serif(30, 800, "#237FD3", { lineHeight: 1.3, letterSpacing: "-0.5px", margin: 0 })}>
          The price you see is the price you pay. Weird that that's a selling point.
        </p>
        <p style={font(17, 400, C.muted, { lineHeight: 1.75, maxWidth: 720, margin: "32px auto 36px" })}>
          We're new, and we're not going to fake it — no stock testimonials, no before-and-afters we can't back up.
        </p>
        <button onClick={() => startQuiz(navigate)} className="vyv-btn-primary" style={{
          background: C.primaryBlue, color: C.white, border: "none",
          borderRadius: 6, padding: "16px 40px",
          fontFamily: "'DM Sans',sans-serif", fontWeight: 700, fontSize: 17,
          cursor: "pointer",
          transition: "background 0.2s",
        }}
          onMouseEnter={e => { e.currentTarget.style.background = C.navy; }}
          onMouseLeave={e => { e.currentTarget.style.background = C.primaryBlue; }}>
          See if you qualify
        </button>
      </div>
    </section>
  );
}

function WhyChooseVyvron() {
  const cards = [
    { icon: "✓", title: "You can verify us.", body: "LegitScript-certified at a public link. Don't trust us — check." },
    { icon: "✚", title: "One honest price.", body: "What you see is what you pay. No hidden tiers, no surprise charges." },
    { icon: "$", title: "No insurance hassle.", body: "Simple cash pay. FSA/HSA accepted. No prior authorizations, no denials." },
    { icon: "◇", title: "Cancel by email.", body: "Cancel by emailing support@vyvron.com — effective at the end of your billing cycle." },
  ];
  return (
    <section className="vyv-why" style={{ background: "#0A1628", padding: "80px 48px" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ textAlign: "center", maxWidth: 760, margin: "0 auto 56px" }}>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            background: "transparent",
            border: `1px solid rgba(35,127,211,0.3)`,
            borderRadius: 100, padding: "6px 16px", marginBottom: 22,
          }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#237FD3", display: "inline-block" }}></span>
            <span style={font(11, 700, "#B8C2D1", { letterSpacing: "0.12em", textTransform: "uppercase" })}>Why people choose Vyvron</span>
          </div>
          <h2 style={serif(42, 700, "#FFFFFF", { letterSpacing: "0.5px", lineHeight: 1.15, marginBottom: 18 })}>
            Clean, clear, and verifiable.
          </h2>
          <p style={font(17, 400, "#CBD5E1", { lineHeight: 1.7 })}>
            A private, provider-guided experience built for patients who want more than a generic online clinic.
          </p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 24 }}>
          {cards.map((c, i) => (
            <div key={i} className="vyv-modern-card vyv-why-card" style={{
              background: "#FFFFFF",
              borderRadius: 16,
              borderTop: `3px solid #237FD3`,
              padding: 32,
              boxShadow: "0 1px 2px rgba(0,0,0,0.06), 0 14px 36px rgba(0,0,0,0.18)",
              transition: "transform 0.2s, box-shadow 0.2s",
            }}
              onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 1px 2px rgba(0,0,0,0.06), 0 22px 44px rgba(0,0,0,0.25)"; }}
              onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 1px 2px rgba(0,0,0,0.06), 0 14px 36px rgba(0,0,0,0.18)"; }}
            >
              <div style={{
                width: 40, height: 40, borderRadius: 10,
                border: `1px solid #237FD3`,
                color: "#237FD3",
                background: "rgba(35,127,211,0.06)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 18, marginBottom: 18, fontWeight: 600,
              }}>{c.icon}</div>
              <h3 style={serif(20, 700, C.navy, { letterSpacing: "0.5px", marginBottom: 10, lineHeight: 1.3 })}>{c.title}</h3>
              <p style={font(14.5, 400, C.muted, { lineHeight: 1.65 })}>{c.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function InspectUs() {
  const claims = [
    {
      title: "LegitScript Certified",
      body: "Independently vetted against telehealth safety and compliance standards — and publicly listed. Don't trust us; verify it.",
      link: {
        label: "Verify on the LegitScript registry →",
        href: "https://legitscript.com/websites/vyvron.com",
      },
    },
    {
      title: "Independent licensed US providers",
      body: "Every eligibility, medication, and treatment decision is made by a provider licensed in your state — never an algorithm. Provider review is required, and not everyone qualifies.",
    },
    {
      title: "One honest price — with a full refund if denied",
      body: "The price you see is the price you pay: no hidden fees, no surprise renewals, no membership add-on. You're charged upfront, then a provider reviews your plan. If you're denied, you receive a full refund.",
    },
    {
      title: "Cancel anytime",
      body: "Cancel by emailing support@vyvron.com — effective at the end of your current billing cycle, with no cancellation fees.",
    },
    {
      title: "Where we operate",
      body: "Available across the United States. Compounded GLP-1 medications are not available to Mississippi residents. State, medication, and pricing availability may vary and can change.",
    },
    {
      title: "About compounded medications",
      body: "Compounded medications are not FDA-approved and are not identical or equivalent to any FDA-approved branded medication. A licensed provider determines whether any treatment is appropriate for you.",
    },
  ];

  return (
    <section id="inspect-us">
      <div className="vyv-inspect-inner">
        <div className="vyv-inspect-heading">
          <p>See for yourself</p>
          <h2>Don't take our word for it. Inspect everything.</h2>
          <div>Most telehealth asks you to trust a logo. We'd rather you check — open any claim below.</div>
        </div>

        <div className="vyv-inspect-list">
          {claims.map((claim) => (
            <details key={claim.title} className="vyv-modern-card vyv-inspect-row">
              <summary>
                <span>{claim.title}</span>
                <i aria-hidden="true">+</i>
              </summary>
              <div className="vyv-inspect-body">
                <p>{claim.body}</p>
                {claim.link && (
                  <a href={claim.link.href} target="_blank" rel="noopener noreferrer">
                    {claim.link.label}
                  </a>
                )}
              </div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function OurPrograms() {
  return (
    <section id="programs" style={{ background: C.champagne, padding: "100px 48px" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ textAlign: "center", maxWidth: 760, margin: "0 auto 56px" }}>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            background: "transparent",
            border: `1px solid rgba(35,127,211,0.3)`,
            borderRadius: 100, padding: "6px 16px", marginBottom: 22,
          }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#237FD3", display: "inline-block" }}></span>
            <span style={font(11, 700, "#5B6B7B", { letterSpacing: "0.12em", textTransform: "uppercase" })}>Our Programs</span>
          </div>
          <h2 style={serif(42, 700, C.navy, { letterSpacing: "0.5px", lineHeight: 1.15, marginBottom: 18 })}>
            Wherever you're starting, start it right.
          </h2>
        </div>

        <div className="vyv-programs-bento">
          <Link to="/start" className="vyv-modern-card vyv-program-card vyv-program-bento-card vyv-program-bento-featured">
            <div className="vyv-program-media vyv-program-media--featured">
              <img
                src={vyvWeightLoss}
                alt="Weight-loss program: capped injector pen, sealed vial and tablet"
                loading="lazy"
                decoding="async"
              />
            </div>
            <div className="vyv-program-content vyv-program-content--featured">
              <p className="vyv-program-eyebrow">Medical Weight Loss</p>
              <h3>GLP-1 care, one honest price.</h3>
              <div className="vyv-program-price vyv-program-price--featured">
                <strong>{PRICING.medicalWeightLoss.founding.display}</strong>
                <s>{PRICING.medicalWeightLoss.standard.display}</s>
                <span>/ 4 weeks · limited-time</span>
              </div>
              <span className="vyv-program-primary-cta">See if you qualify</span>
            </div>
          </Link>

          <Link to="/microdose" className="vyv-modern-card vyv-program-card vyv-program-bento-card vyv-program-bento-side">
            <div className="vyv-program-media">
              <img
                src={vyvMicrodose}
                alt="Microdose program: sealed amber vial with navy cap on a tray"
                loading="lazy"
                decoding="async"
              />
            </div>
            <div className="vyv-program-content">
              <p className="vyv-program-eyebrow">Microdose</p>
              <h3>Optimize quietly.</h3>
              <div className="vyv-program-price">
                <strong>{PRICING.microdose.founding.display}</strong>
                <span>/ 4 wks</span>
              </div>
              <p className="vyv-program-subcopy">Cellular energy &amp; metabolic support.</p>
              <span className="vyv-program-arrow" aria-hidden="true">→</span>
            </div>
          </Link>

          <Link to="/longevity" className="vyv-modern-card vyv-program-card vyv-program-bento-card vyv-program-bento-side">
            <div className="vyv-program-media">
              <img
                src={vyvLongevity}
                alt="Longevity program: sealed vials and dropper bottle"
                loading="lazy"
                decoding="async"
              />
            </div>
            <div className="vyv-program-content">
              <p className="vyv-program-eyebrow">Longevity</p>
              <h3>Build the next ten years.</h3>
              <div className="vyv-program-price">
                <strong>{PRICING.longevity.products.nadCellularRecharge.founding.display}</strong>
                <span>/ 4 wks</span>
              </div>
              <p className="vyv-program-subcopy">NAD+, Sermorelin, metabolism support.</p>
              <span className="vyv-program-arrow" aria-hidden="true">→</span>
            </div>
          </Link>
        </div>

        <div className="vyv-program-pricing-strip">
          <div>
            <h3>See exactly what's included — before you pay.</h3>
            <p>Provider evaluation, medication if prescribed, and ongoing access — one price, no hidden fees, no surprise renewals.</p>
          </div>
          <a href="#pricing">See what's included</a>
        </div>

        <p style={font(12.5, 400, "#5B6B7B", { lineHeight: 1.7, textAlign: "center", maxWidth: 760, margin: "32px auto 0" })}>
          All therapies require a prescription from a licensed provider. Compounded medications are patient-specific and are not FDA-approved. Consultation required. Availability may vary by state.
        </p>
      </div>
    </section>
  );
}


function HowItWorks() {
  return (
    <section id="how-it-works" style={{ background: C.lightBg, padding: "100px 48px" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div className="vyv-pricing-shell" style={{
          display: "inline-flex", alignItems: "center", gap: 8,
          background: "transparent",
          border: `1px solid rgba(35,127,211,0.3)`,
          borderRadius: 100, padding: "6px 16px", marginBottom: 16,
        }}>
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#237FD3", display: "inline-block" }}></span>
          <span style={font(11, 700, "#5B6B7B", { letterSpacing: "0.12em", textTransform: "uppercase" })}>The Process</span>
        </div>
        <h2 style={{ ...serif(42, 700, C.navy), marginBottom: 16 }}>How It Works</h2>
        <p style={font(17, 400, C.muted, { maxWidth: 560, lineHeight: 1.6, marginBottom: 60 })}>
          A simple online process. Vyvron connects you with an independent licensed medical provider for evaluation. Treatment is provided only when clinically appropriate.
        </p>
        <div className="vyv-process-steps">
          {HOW.map((s, i) => (
            <div key={i} style={{ position: "relative", minWidth: 0 }}>
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "center",
                width: 34, height: 34, borderRadius: "50%",
                background: C.navy, marginBottom: 20,
                ...serif(15, 700, "#FFFFFF", { lineHeight: 1, letterSpacing: 0 }),
              }}>{s.n}</div>
              {i < HOW.length - 1 && <span className="vyv-process-arrow" aria-hidden="true">→</span>}
              <h3 style={serif(17, 700, C.navy, { marginBottom: 10, lineHeight: 1.3 })}>{s.title}</h3>
              <p style={font(14, 400, C.muted, { lineHeight: 1.65 })}>{s.body}</p>
            </div>
          ))}
        </div>
        <p style={font(14, 400, C.muted, { lineHeight: 1.65, marginTop: 32, maxWidth: 860 })}>
          Most evaluations are completed online through your intake, which a licensed provider reviews. However, some states may require a synchronous (real-time) consultation — by phone or video — before a prescription can be issued. If a synchronous consultation is required to comply with your state's regulations, or if additional information is needed, a licensed provider affiliated with our network will contact you to arrange one.
        </p>
        <p style={font(13, 500, C.muted, { textAlign: "center", marginTop: 32, lineHeight: 1.6, maxWidth: 720, marginLeft: "auto", marginRight: "auto" })}>
          {DISCLAIMERS.cta}
        </p>
      </div>
    </section>
  );
}

function Pricing() {
  const ledgerGroups = [
    {
      heading: "What's included in your plan:",
      items: [
        "Private online evaluation by an independent, licensed U.S. provider",
        "Medication if prescribed — included and shipped to you (compounded medications are not FDA-approved)",
        "Ongoing clinical support and check-ins",
        "Refill management",
        "Discreet shipping from a licensed U.S. pharmacy partner",
      ],
    },
    {
      heading: "What you won't find:",
      items: [
        "No hidden fees or surprise charges — the price you see is the price you pay",
        "No \"from $\" asterisk — the number you see is the whole number",
        "No insurance required — cash pay; FSA/HSA accepted",
        "No membership fee on top of your plan",
        "No long-term contract — cancel by emailing support@vyvron.com, effective at the end of your billing cycle",
      ],
      note: "Optional add-ons, like Care Coaching, are available separately.",
    },
    {
      heading: "Pricing terms:",
      items: [
        PRICE_COPY.medicalWeightLossPricingLine,
        PRICE_COPY.medicalWeightLossTermsLine,
      ],
      wide: true,
    },
  ];

  return (
    <section id="pricing" style={{ background: "#F7F4EE", padding: "100px 48px" }}>
      <div style={{ maxWidth: 820, margin: "0 auto", textAlign: "center" }}>
        <div style={{
          background: C.white,
          border: "1px solid rgba(10,22,40,0.12)",
          borderRadius: 18,
          padding: "clamp(32px, 6vw, 56px)",
          boxShadow: "0 18px 48px rgba(10,22,40,0.10)",
        }}>
          <p style={font(12, 700, C.navy, { letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 18 })}>
            MEDICAL WEIGHT LOSS PRICING
          </p>
          <h2 style={{ ...serif(42, 700, C.navy), marginBottom: 20, lineHeight: 1.18 }}>
            {PRICE_COPY.medicalWeightLossPricingLine}
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 16, textAlign: "left", margin: "0 auto 28px" }}>
            {ledgerGroups.map((group) => (
              <div key={group.heading} style={{
                gridColumn: group.wide ? "1 / -1" : undefined,
                background: "#FAF7F2",
                border: "1px solid rgba(10,22,40,0.08)",
                borderRadius: 12,
                padding: "20px 22px",
              }}>
                <h3 style={serif(16, 700, C.navy, { letterSpacing: "0", lineHeight: 1.35, margin: "0 0 12px" })}>
                  {group.heading}
                </h3>
                <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 9 }}>
                  {group.items.map((item) => (
                    <li key={item} style={{ ...font(13.5, 400, C.muted, { lineHeight: 1.5 }), display: "flex", alignItems: "flex-start", gap: 9 }}>
                      <span aria-hidden="true" style={{ color: "#237FD3", fontSize: 14, fontWeight: 800, lineHeight: 1.5, flex: "0 0 auto" }}>✓</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                {group.note && (
                  <p style={{ ...font(12.5, 400, C.muted, { lineHeight: 1.5 }), fontStyle: "italic", margin: "13px 0 0" }}>
                    {group.note}
                  </p>
                )}
              </div>
            ))}
          </div>
          <p style={{ ...font(12.5, 400, C.muted, { lineHeight: 1.6 }), margin: "0 auto 8px", maxWidth: 690 }}>
            {PRICE_COPY.roscaBillingLine("medicalWeightLoss")}
          </p>
          <p style={{ ...font(12.5, 400, C.muted, { lineHeight: 1.6 }), margin: "0 auto 22px", maxWidth: 690 }}>
            An independent licensed provider decides whether treatment is appropriate; treatment is not guaranteed. See our <Link to="/refund-policy" style={{ color: C.navy, textDecoration: "underline" }}>Refund Policy</Link> for cancellation and refund details.
          </p>
          <p style={{ ...font(12, 400, C.muted, { lineHeight: 1.6 }), margin: "0 auto 22px", maxWidth: 690 }}>
            If you cancel, you won't be billed for the next cycle; a cycle whose medication has already been ordered isn't refundable.
          </p>
          <a href="/start" className="vyv-btn-primary" style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 48,
            background: C.primaryBlue,
            color: "#FFFFFF",
            borderRadius: 8,
            padding: "14px 24px",
            ...font(15, 700, "#FFFFFF", { letterSpacing: "0.2px", textDecoration: "none" }),
          }}>
            See full pricing &amp; how it works →
          </a>
          <p style={{ ...font(12.5, 400, C.muted, { lineHeight: 1.55 }), margin: "14px auto 0", maxWidth: 620 }}>
            Your answers go to an independent, licensed U.S. provider for review. Vyvron is the platform — not the provider or pharmacy — and treatment is not guaranteed.
          </p>
        </div>
        <p className="vyv-disclosure-text vyv-disclosure-text--light" style={font(12.5, 400, C.muted, {
          lineHeight: 1.7, maxWidth: 720, margin: "18px auto 0", textAlign: "center",
        })}>
          Compounded medications are not FDA-approved. Vyvron is a brand and access platform — not a medical provider, pharmacy, or insurer.
        </p>
      </div>
    </section>
  );
}


function TrustCredibility() {
  const ICON_BLUE = "#237FD3";
  const stroke = { fill: "none", stroke: ICON_BLUE, strokeWidth: 1.5, strokeLinecap: "round", strokeLinejoin: "round" };
  const ShieldIcon = (
    <svg viewBox="0 0 32 32" width="30" height="30" {...stroke}>
      <path d="M16 4 L26 8 V16 C26 22 21.5 26.5 16 28 C10.5 26.5 6 22 6 16 V8 Z" />
      <path d="M12 16 L15 19 L20.5 13.5" />
    </svg>
  );
  const StethoscopeIcon = (
    <svg viewBox="0 0 32 32" width="30" height="30" {...stroke}>
      <path d="M8 5 v6 a6 6 0 0 0 12 0 V5" />
      <path d="M14 17 v4 a6 6 0 0 0 6 6 a5 5 0 0 0 5 -5 v-2.6" />
      <circle cx="25" cy="13.4" r="2.4" />
    </svg>
  );
  const LockIcon = (
    <svg viewBox="0 0 32 32" width="30" height="30" {...stroke}>
      <rect x="7" y="14" width="18" height="14" rx="2.5" />
      <path d="M11 14 V10 a5 5 0 0 1 10 0 v4" />
      <circle cx="16" cy="21" r="1.6" fill={ICON_BLUE} stroke="none" />
    </svg>
  );
  const MapPinIcon = (
    <svg viewBox="0 0 32 32" width="30" height="30" {...stroke}>
      <path d="M16 28 C16 28 25 19.5 25 13 a9 9 0 0 0 -18 0 C7 19.5 16 28 16 28 Z" />
      <circle cx="16" cy="13" r="3.2" />
    </svg>
  );

  const trustItems = [
    { icon: ShieldIcon, title: "Private & Secure", sub: "Protected through secure intake" },
    { icon: StethoscopeIcon, title: "Independent Licensed Providers", sub: "Independent medical review" },
    { icon: LockIcon, title: "Private by Design", sub: "Discreet evaluation & shipping" },
    { icon: MapPinIcon, title: "Available across the U.S.", sub: "Compounded medications aren't available in Mississippi." },
  ];

  return (
    <section className="vyv-trust-credibility" style={{ background: "#0A1628", padding: "100px 48px" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ textAlign: "center", maxWidth: 760, margin: "0 auto 64px" }}>
          <div className="vyv-modern-image vyv-clinical-image" style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            background: "transparent",
            border: `1px solid rgba(35,127,211,0.3)`,
            borderRadius: 100, padding: "6px 16px", marginBottom: 22,
          }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#237FD3", display: "inline-block" }}></span>
            <span style={font(11, 700, "#B8C2D1", { letterSpacing: "0.14em", textTransform: "uppercase" })}>Trust &amp; Credibility</span>
          </div>
          <h2 style={serif(42, 700, "#FFFFFF", { letterSpacing: "0.5px", lineHeight: 1.15, marginBottom: 18 })}>
            Built on Trust. Designed for Patients.
          </h2>
          <p style={font(17, 400, "#CBD5E1", { lineHeight: 1.7 })}>
            Vyvron is designed around privacy, licensed provider review, and a patient experience built for clarity from the first step.
          </p>
        </div>

        <div style={{ display: "flex", justifyContent: "center", marginBottom: 48 }}>
          <div style={{
            maxWidth: 480, width: "100%",
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 12, overflow: "hidden",
            boxShadow: "0 1px 2px rgba(0,0,0,0.10), 0 18px 40px rgba(0,0,0,0.25)",
          }}>
            <img
              src={sceneClinical}
              sizes="(max-width: 720px) calc(100vw - 40px), 480px"
              alt="A calm, private telehealth care setting."
              loading="lazy"
              decoding="async"
              style={{
              display: "block", width: "100%", height: 360,
              objectFit: "cover", objectPosition: "center 30%",
            }} />
          </div>
        </div>

        <div className="vyv-trust-row" style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 24 }}>
          {trustItems.map((item, i) => (
            <div key={i} className="vyv-modern-card vyv-trust-card" style={{
              textAlign: "center",
              padding: "36px 22px",
              background: "rgba(255,255,255,0.03)",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 18,
            }}>
              <div style={{
                width: 64, height: 64, borderRadius: "50%",
                border: `1px solid rgba(35,127,211,0.45)`,
                background: "rgba(35,127,211,0.10)",
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                marginBottom: 18,
              }}>{item.icon}</div>
              <h3 style={serif(19, 700, "#FFFFFF", { letterSpacing: "0.3px", lineHeight: 1.3, marginBottom: 8 })}>
                {item.title}
              </h3>
              <p style={font(13.5, 400, "#B8C2D1", { lineHeight: 1.6 })}>
                {item.sub}
              </p>
            </div>
          ))}
        </div>

        {/* Provider trust block — answers "real clinic vs pill mill" without naming a specific advisor */}
        <div id="vyv-provider-trust" style={{ marginTop: 56, borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 48 }}>
          <div style={{ maxWidth: 760, margin: "0 auto", textAlign: "center" }}>
            {/* Icon */}
            <div aria-hidden="true" style={{
              width: 56, height: 56, borderRadius: "50%",
              border: "1px solid rgba(35,127,211,0.45)", background: "rgba(35,127,211,0.1)",
              display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 20,
            }}>
              <svg viewBox="0 0 32 32" width="28" height="28" fill="none" stroke="#237FD3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="16" cy="10" r="5" />
                <path d="M6 28a10 10 0 0 1 20 0" />
                <path d="M20 19l2 2 4-4" />
              </svg>
            </div>
            <h3 style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 26, fontWeight: 700, color: "#FFFFFF", lineHeight: 1.3, letterSpacing: "0.3px", marginBottom: 14 }}>
              Every intake is reviewed by an independent,<br />U.S. state-licensed provider — not a bot.
            </h3>
            <p style={{ fontFamily: "Inter,sans-serif", fontSize: 15, color: "#CBD5E1", lineHeight: 1.75, maxWidth: 620, margin: "0 auto 28px" }}>
              No automated approvals. No rubber-stamp prescriptions. Every Vyvron patient evaluation is reviewed by an independent, state-licensed medical provider who evaluates your intake on its clinical merits. Treatment is only prescribed when medically appropriate — and that decision belongs to your provider, not an algorithm.
            </p>
            {/* Micro-trust row */}
            <div style={{ display: "inline-flex", flexWrap: "wrap", alignItems: "center", justifyContent: "center", gap: "8px 20px" }}>
              <span style={{ fontFamily: "Inter,sans-serif", fontSize: 12, fontWeight: 600, color: "#94A3B8", letterSpacing: "0.05em" }}>Independent providers</span>
              <span aria-hidden="true" style={{ color: "rgba(255,255,255,0.2)" }}>·</span>
              <span style={{ fontFamily: "Inter,sans-serif", fontSize: 12, fontWeight: 600, color: "#94A3B8", letterSpacing: "0.05em" }}>State-licensed in all 50 states</span>
              <span aria-hidden="true" style={{ color: "rgba(255,255,255,0.2)" }}>·</span>
              <span style={{ fontFamily: "Inter,sans-serif", fontSize: 12, fontWeight: 600, color: "#94A3B8", letterSpacing: "0.05em" }}>Clinically appropriate prescribing only</span>
            </div>
          </div>
        </div>
        {/* END provider trust block */}

        {/* LegitScript certification seal placeholder */}
        <div style={{ display: "flex", justifyContent: "center", marginTop: 44 }}>
          {/* Reserved for a future certification seal. */}
        </div>

        <p style={font(14, 400, "#B8C2D1", { lineHeight: 1.7, textAlign: "center", maxWidth: 680, margin: "28px auto 0" })}>
          No surprise charges. The price you see is the price you pay — billed at the start of each term, cancel by emailing support@vyvron.com, effective at the end of your billing cycle.
        </p>

        {SHOW_PLACEHOLDER_SLOTS && (
          <div style={{
            marginTop: 72, paddingTop: 44,
            borderTop: "1px solid rgba(255,255,255,0.08)",
            textAlign: "center",
          }}>
            <div style={font(11, 700, "#B8C2D1", { letterSpacing: "0.18em", textTransform: "uppercase", marginBottom: 22 })}>
              Internal Only — Partner Logo Slots
            </div>
            <div className="vyv-trust-logos" style={{
              display: "grid", gridTemplateColumns: "repeat(4, 1fr)",
              gap: 20, maxWidth: 880, margin: "0 auto 18px",
            }}>
              {[1, 2, 3, 4].map(n => (
                <div key={n} style={{
                  height: 64,
                  background: "rgba(255,255,255,0.05)",
                  border: "1px dashed rgba(255,255,255,0.14)",
                  borderRadius: 10,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  ...font(11, 600, "rgba(184,194,209,0.7)", { letterSpacing: "0.1em" }),
                }}>SLOT {n}</div>
              ))}
            </div>
            <p style={font(12, 400, "rgba(184,194,209,0.85)", { lineHeight: 1.6, fontStyle: "italic", maxWidth: 640, margin: "0 auto" })}>
              Internal use only. Slot will display verified partner, press, or certification logos when supplied.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}

function QualificationJourney() {
  const steps = [
    {
      title: "Take the quiz",
      body: "A few private questions about your health and goals — about 2 minutes.",
    },
    {
      title: "Complete your intake",
      body: "Securely share your health history for provider review.",
    },
    {
      title: "Independent provider review",
      body: "A licensed U.S. provider reviews your information and decides whether treatment is appropriate. Treatment isn't guaranteed.",
    },
    {
      title: "Prescription & fulfillment (if appropriate)",
      body: "If a provider prescribes treatment, a licensed U.S. pharmacy partner fills your medication.",
    },
    {
      title: "Discreet shipping",
      body: "Delivered to your door in plain, unbranded packaging.",
    },
    {
      title: "Ongoing care & refills",
      body: "Continued check-ins, refill management, and support at support@vyvron.com along the way.",
    },
  ];

  return (
    <section
      id="vyv-qualification-journey"
      aria-labelledby="vyv-qualification-journey-title"
      style={{ background: C.champagne, padding: "100px 48px" }}
    >
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ textAlign: "center", maxWidth: 760, margin: "0 auto 56px" }}>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            background: "rgba(35,127,211,0.06)",
            border: "1px solid rgba(35,127,211,0.3)",
            borderRadius: 100, padding: "6px 16px", marginBottom: 22,
          }}>
            <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: "50%", background: C.blue, display: "inline-block" }} />
            <span style={font(11, 700, C.navy, { letterSpacing: "0.12em", textTransform: "uppercase" })}>
              Your Journey
            </span>
          </div>
          <h2
            id="vyv-qualification-journey-title"
            style={serif(42, 700, C.navy, { letterSpacing: "0.5px", lineHeight: 1.15, marginBottom: 16 })}
          >
            What happens after you qualify.
          </h2>
          <p style={font(16, 400, C.muted, { lineHeight: 1.7, maxWidth: 620, margin: "0 auto" })}>
            No mystery, no runaround — here's exactly how it works, start to finish.
          </p>
        </div>

        <ol className="vyv-journey-timeline" style={{ listStyle: "none", maxWidth: 860, margin: "0 auto", padding: 0 }}>
          {steps.map((step, index) => (
            <li
              key={step.title}
              className="vyv-journey-step"
              style={{ display: "flex", alignItems: "stretch", gap: 22, marginBottom: index === steps.length - 1 ? 0 : 24 }}
            >
              <div className="vyv-journey-marker-wrap" aria-hidden="true">
                <span className="vyv-journey-marker">
                  {index + 1}
                </span>
              </div>
              <article className="vyv-modern-card vyv-journey-card" style={{
                flex: 1,
                background: C.white,
                border: "1px solid rgba(10,22,40,0.10)",
                borderRadius: 16,
                padding: "26px 30px",
                boxShadow: "0 10px 30px rgba(10,22,40,0.06)",
              }}>
                <h3 style={serif(19, 700, C.navy, { letterSpacing: "0.1px", lineHeight: 1.35, marginBottom: 7 })}>
                  {step.title}
                </h3>
                <p style={font(14.5, 400, C.muted, { lineHeight: 1.7, margin: 0 })}>
                  {step.body}
                </p>
              </article>
            </li>
          ))}
        </ol>

        <p className="vyv-journey-disclosure" style={font(12.5, 400, C.muted, {
          lineHeight: 1.65, textAlign: "center", maxWidth: 720, margin: "30px auto 0",
        })}>
          Compounded medications are not FDA-approved and aren't available to Mississippi residents. Provider review required; treatment is not guaranteed.
        </p>
      </div>
    </section>
  );
}

// Homepage FAQ — questions + plain-text answers live in src/lib/homeFaqs.js
// (single source shared with the static FAQPage JSON-LD in index.html; the
// prerender step regenerates that block at build time so they never drift).
// Only the JSX answer renderers live here, keyed by question `q` so a
// mismatch fails loudly instead of silently pairing the wrong answer.
const HOME_FAQ_ANSWERS = {
  "How does Vyvron work?": (link) => <>Vyvron is a telehealth access platform, not a medical provider or pharmacy. You complete a private online evaluation, and an independent licensed medical provider reviews your health history to determine whether prescription treatment is clinically appropriate. If a provider prescribes treatment, your medication is filled and shipped by a licensed pharmacy.</>,
  "Who prescribes the medication?": (link) => <>Independent licensed medical providers — not Vyvron. Vyvron connects you with providers licensed in your state; all eligibility, medication, and treatment decisions are made solely by those providers. Prescription treatment is not guaranteed.</>,
  "Are these medications FDA-approved?": (link) => <>Some medications available through our provider network may be compounded. Compounded medications are not FDA-approved and are not identical or equivalent to any FDA-approved branded medication. An independent licensed provider determines which option, if any, is clinically appropriate for you.</>,
  "What states is Vyvron available in?": (link) => <>Vyvron connects patients with licensed providers in all 50 states plus Washington, D.C. Available nationwide; in certain states, evaluation requires a scheduled video visit (AR, DC, DE, KS, MS, NM, RI, WV). Compounded GLP-1 medications are not available to Mississippi residents; Mississippi residents may be eligible for branded options only. State, medication, and pricing availability may vary and can change.</>,
  "How does billing work?": (link) => {
    const [before, email, after] = HOME_FAQ_DATA.find(f => f.q === "How does billing work?").text.split(/(support@vyvron.com)/);
    return <>{before}<a href={`mailto:${email}`} style={link}>{email}</a>{after}</>;
  },
  "Is Vyvron legitimate?": (link) => <>Yes. Vyvron is LegitScript-certified, meaning the platform has been independently vetted against telehealth compliance standards. You can verify the certification yourself — read our guide on <Link to="/learn/what-is-legitscript-certification" style={link}>how to verify a LegitScript certification</Link>, or click the LegitScript seal in our site footer.</>,
};

// Resources callout — drives visitors into the Learn hub (/learn) with three
// featured guides. Internal-linking boost for SEO / AI-search citation.
// Cards come from the shared article list in src/lib/learnArticles.js
// (featured flag + newest-fill), so they stay in sync with /learn.
const LEARN_FEATURED = getFeaturedArticles();

function LearnCallout() {
  return (
    <section id="vyv-learn-callout" aria-label="Learn hub resources" style={{ background: C.bg, padding: "100px 48px" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ textAlign: "center", maxWidth: 760, margin: "0 auto 48px" }}>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            background: "transparent",
            border: `1px solid rgba(35,127,211,0.3)`,
            borderRadius: 100, padding: "6px 16px", marginBottom: 22,
          }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#237FD3", display: "inline-block" }}></span>
            <span style={font(11, 700, "#5B6B7B", { letterSpacing: "0.12em", textTransform: "uppercase" })}>Resources</span>
          </div>
          <h2 style={serif(42, 700, C.navy, { letterSpacing: "0.5px", lineHeight: 1.15, marginBottom: 16 })}>
            Do your homework. We'll help.
          </h2>
          <p style={font(16, 400, C.muted, { lineHeight: 1.7, maxWidth: 620, margin: "0 auto" })}>
            Straight answers on GLP-1 treatment, provider legitimacy, and pricing — no spin, no sales pitch. Start with these guides from Vyvron Learn.
          </p>
        </div>

        <div className="vyv-learn-cards" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 24, marginBottom: 40 }}>
          {LEARN_FEATURED.map(({ to, tag, title, summary }) => (
            <Link key={to} to={to} style={{ textDecoration: "none", display: "block" }}>
              <article className="vyv-modern-card vyv-learn-card" style={{
                background: C.white, borderRadius: 16,
                border: "1px solid rgba(10,22,40,0.09)",
                padding: "30px 28px", height: "100%",
                display: "flex", flexDirection: "column", gap: 12,
                transition: "box-shadow 0.2s, border-color 0.2s",
                cursor: "pointer",
              }}
                onMouseEnter={e => {
                  e.currentTarget.style.boxShadow = "0 4px 24px rgba(35,127,211,0.12)";
                  e.currentTarget.style.borderColor = "#237FD3";
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.boxShadow = "none";
                  e.currentTarget.style.borderColor = "rgba(10,22,40,0.09)";
                }}>
                <span style={font(11, 700, "#0A1628", { letterSpacing: "0.12em", textTransform: "uppercase" })}>{tag}</span>
                <h3 style={serif(19, 700, C.navy, { lineHeight: 1.3 })}>{title}</h3>
                <p style={font(14, 400, C.muted, { lineHeight: 1.7, flex: 1 })}>{summary}</p>
                <span aria-hidden style={{ color: "#237FD3", fontSize: 18, fontWeight: 600 }}>→</span>
              </article>
            </Link>
          ))}
        </div>

        <div style={{ textAlign: "center" }}>
          <Link to="/learn" style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            background: "transparent", color: C.navy,
            border: `1px solid ${C.navy}`, borderRadius: 6,
            padding: "13px 28px", textDecoration: "none",
            fontFamily: "'DM Sans', sans-serif", fontSize: 15, fontWeight: 600,
            transition: "color 0.2s, border-color 0.2s",
          }}
            onMouseEnter={e => { e.currentTarget.style.color = "#0A1628"; e.currentTarget.style.borderColor = "#237FD3"; }}
            onMouseLeave={e => { e.currentTarget.style.color = C.navy; e.currentTarget.style.borderColor = C.navy; }}>
            Browse all guides →
          </Link>
        </div>
      </div>
    </section>
  );
}

const HOME_FAQS = HOME_FAQ_DATA.map((f) => {
  const a = HOME_FAQ_ANSWERS[f.q];
  if (!a) throw new Error(`No JSX answer for FAQ question: ${f.q}`);
  return { ...f, a };
});

function FAQSection() {
  const [open, setOpen] = useState(null);
  const link = { color: "#0A1628" };
  const faqs = HOME_FAQS;
  // FAQPage JSON-LD is NOT injected at runtime — it ships statically in
  // index.html <head> (between home-faq-jsonld markers), generated from the
  // same src/lib/homeFaqs.js data, so crawlers see it without running JS.

  return (
    <section id="faq" style={{ background: C.champagne, padding: "100px 48px" }}>
      <div style={{ maxWidth: 860, margin: "0 auto" }}>
        <div style={{ textAlign: "center", maxWidth: 760, margin: "0 auto 48px" }}>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            background: "transparent",
            border: `1px solid rgba(35,127,211,0.3)`,
            borderRadius: 100, padding: "6px 16px", marginBottom: 22,
          }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#237FD3", display: "inline-block" }}></span>
            <span style={font(11, 700, "#5B6B7B", { letterSpacing: "0.12em", textTransform: "uppercase" })}>Common Questions</span>
          </div>
          <h2 style={serif(42, 700, C.navy, { letterSpacing: "0.5px", lineHeight: 1.15 })}>
            Frequently Asked Questions
          </h2>
        </div>

        <div style={{ borderTop: `1px solid ${C.border}` }}>
          {faqs.map((f, i) => (
            <div key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
              <button
                onClick={() => setOpen(open === i ? null : i)}
                aria-expanded={open === i}
                style={{
                  width: "100%", background: "transparent", border: "none",
                  padding: "22px 8px", cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16,
                  textAlign: "left",
                  ...font(17, 600, C.navy, { fontFamily: "'DM Sans', sans-serif" }),
                }}>
                <span>{f.q}</span>
                <span aria-hidden style={{ color: "#237FD3", fontSize: 20, fontWeight: 400, flexShrink: 0, lineHeight: 1 }}>{open === i ? "−" : "+"}</span>
              </button>
              {open === i && (
                <p style={font(15.5, 400, C.muted, { lineHeight: 1.75, padding: "0 8px 24px" })}>{f.a(link)}</p>
              )}
            </div>
          ))}
        </div>

        <p style={font(15, 400, C.muted, { lineHeight: 1.7, textAlign: "center", marginTop: 36 })}>
          Have another question? Contact us at <a href="mailto:support@vyvron.com" style={link}>support@vyvron.com</a>.
        </p>
      </div>
    </section>
  );
}

// Mid-page soft-capture band — sits immediately before the final CTA band.
// Submits inline to the same GHL funnel (does NOT open the modal).
function CaptureBand() {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | submitting | success | error
  const bot = useBotDefense();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || state === "submitting") return;
    setState("submitting");
    try {
      await submitLead({ firstName, lastName, email, hp: bot.hp, renderedAt: bot.renderedAt });
      trackLeadEvent("lead_capture_band_submit"); // no PII
      setState("success");
    } catch {
      setState("error");
    }
  };

  return (
    <section id="vyv-capture-band" style={{ background: "#FAF7F2", padding: "72px 48px" }}>
      <div style={{ maxWidth: 560, margin: "0 auto", textAlign: "center" }}>
        <div style={{ fontFamily: "'DM Sans',sans-serif", fontWeight: 700, fontSize: 11, color: "#0A1628", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 14 }}>
          NO COMMITMENT
        </div>
        <h2 className="vyv-capture-headline" style={{ fontFamily: "'DM Sans',sans-serif", fontWeight: 700, fontSize: 32, color: "#0A1628", lineHeight: 1.2, marginBottom: 14 }}>
          Not ready for a full evaluation? Start here.
        </h2>
        <p style={{ fontFamily: "'Inter',sans-serif", fontSize: 16, color: "#4A5568", lineHeight: 1.7, maxWidth: 480, margin: "0 auto 28px" }}>
          See what's available in your state, how the process works, and what independent licensed providers look for.
        </p>

        {state === "success" ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, flexWrap: "wrap" }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#237FD3" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <circle cx="12" cy="12" r="10" />
              <path d="M8 12.5l2.5 2.5L16 9.5" />
            </svg>
            <span style={{ fontFamily: "'DM Sans',sans-serif", fontWeight: 700, fontSize: 18, color: "#0A1628" }}>
              Check your inbox — your options are on their way.
            </span>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="vyv-capture-form" style={{ display: "flex", flexDirection: "column", gap: 10, maxWidth: 400, margin: "0 auto" }}>
            <HoneypotField value={bot.hp} onChange={bot.setHp} />
            <div style={{ display: "flex", gap: 10 }}>
              <input
                type="text"
                required
                placeholder="First name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                autoComplete="given-name"
                style={{
                  flex: 1, height: 46, background: "#FFFFFF",
                  border: "1px solid #0A1628", borderRadius: 6, padding: "0 14px",
                  fontFamily: "'Inter',sans-serif", fontSize: 15, color: "#0A1628", minWidth: 0,
                }}
              />
              <input
                type="text"
                required
                placeholder="Last name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                autoComplete="family-name"
                style={{
                  flex: 1, height: 46, background: "#FFFFFF",
                  border: "1px solid #0A1628", borderRadius: 6, padding: "0 14px",
                  fontFamily: "'Inter',sans-serif", fontSize: 15, color: "#0A1628", minWidth: 0,
                }}
              />
            </div>
            <input
              type="email"
              required
              placeholder="Your email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              style={{
                width: "100%", height: 46, background: "#FFFFFF",
                border: "1px solid #0A1628", borderRadius: 6, padding: "0 14px",
                fontFamily: "'Inter',sans-serif", fontSize: 15, color: "#0A1628",
                boxSizing: "border-box",
              }}
            />
            <button type="submit" disabled={state === "submitting"} style={{
              width: "100%", height: 46, background: "#0A1628", color: "#FAF7F2",
              border: "none", borderRadius: 6, padding: "0 20px",
              fontFamily: "'DM Sans',sans-serif", fontWeight: 700, fontSize: 15,
              cursor: state === "submitting" ? "wait" : "pointer",
              opacity: state === "submitting" ? 0.75 : 1,
            }}>
              {state === "submitting" ? "Sending…" : "Get my options"}
            </button>
          </form>
        )}

        {state === "error" && (
          <p style={{ fontFamily: "'Inter',sans-serif", fontSize: 13, color: "#DC2626", lineHeight: 1.5, marginTop: 12 }}>
            Something went wrong — please try again or email us at support@vyvron.com.
          </p>
        )}

        <p style={{ fontFamily: "'Inter',sans-serif", fontSize: 12, color: "#9CA3AF", lineHeight: 1.6, marginTop: 18 }}>
          Private &amp; secure · Independent licensed providers · Cancel by emailing support@vyvron.com, effective at the end of your billing cycle. · Not available to Mississippi residents for compounded medications.
        </p>
      </div>
    </section>
  );
}

function FinalCTA() {
  const handleCTA = openIntake;
  return (
    <section className="vyv-final-cta vyv-surface-dark" style={{
      position: "relative",
      "--vyv-final-cta-mobile": `linear-gradient(rgba(10,22,40,0.82), rgba(10,22,40,0.82)), url(${lifestyleWalk750})`,
      "--vyv-final-cta-desktop": `linear-gradient(rgba(10,22,40,0.82), rgba(10,22,40,0.82)), url(${lifestyleWalk1200})`,
      backgroundColor: C.navy,
      backgroundPosition: "center",
      backgroundSize: "cover",
      backgroundRepeat: "no-repeat",
      padding: "100px 48px", textAlign: "center",
    }}>
      <div style={{ maxWidth: 680, margin: "0 auto", position: "relative" }}>
        <div aria-hidden style={{ width: 48, height: 3, background: "#237FD3", margin: "0 auto 22px" }} />
        <div style={font(12, 700, "#B8C2D1", { letterSpacing: "0.16em", textTransform: "uppercase", marginBottom: 18 })}>Begin Your Evaluation</div>
        <h2 style={{ ...serif(48, 700, "white", { letterSpacing: "0.5px", lineHeight: 1.15 }), marginBottom: 20 }}>
          Ready to Take the First Step?
        </h2>
        <p style={font(17, 400, "#E5E7EB", { lineHeight: 1.7, marginBottom: 40 })}>
          Start a private online evaluation and connect with licensed provider review for weight-management treatment options when clinically appropriate.
        </p>
        <button onClick={handleCTA} className="vyv-btn-primary" style={{
          background: C.primaryBlue, color: C.white, border: "none",
          borderRadius: 6, padding: "14px 32px",
          ...font(15, 500, C.white, { letterSpacing: "0.5px" }),
          cursor: "pointer", transition: "background 0.2s",
        }}
          onMouseEnter={e => e.currentTarget.style.background = C.navy}
          onMouseLeave={e => e.currentTarget.style.background = C.primaryBlue}>
          See if you qualify
        </button>
        <p className="vyv-disclosure-text vyv-disclosure-text--dark" style={font(13, 500, "#CBD5E1", { marginTop: 20, lineHeight: 1.6, maxWidth: 560, marginLeft: "auto", marginRight: "auto" })}>
          Provider review required. Prescription treatment is not guaranteed.
        </p>
        <p className="vyv-disclosure-text vyv-disclosure-text--dark" style={font(12, 400, "#B8C2D1", { marginTop: 8, lineHeight: 1.6, maxWidth: 600, marginLeft: "auto", marginRight: "auto" })}>
          Available to eligible patients only. State, medication, and pharmacy availability may vary. Vyvron is not a medical provider or pharmacy.
        </p>
      </div>
    </section>
  );
}

function ComplianceNotice() {
  return (
    <section style={{ background: C.lightBg, borderTop: `1px solid ${C.border}`, padding: "48px 48px" }}>
      <div style={{ maxWidth: 980, margin: "0 auto" }}>
        <div style={font(11, 700, C.muted, { letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 14, textAlign: "center" })}>
          Important Compliance Notice
        </div>
        <div className="vyv-modern-card vyv-compliance-card" style={{
          background: C.white, border: `1px solid ${C.border}`, borderRadius: 16,
          padding: "28px 32px",
        }}>
          <p style={font(14, 500, "#1F2937", { lineHeight: 1.7 })}>
            {DISCLAIMERS.long}
          </p>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="vyv-surface-dark" style={{ background: C.navy, padding: "72px 48px 36px" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: 48, paddingBottom: 48, borderBottom: "1px solid rgba(255,255,255,0.10)" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
              <span style={serif(20, 700, C.white, { letterSpacing: "-0.5px", lineHeight: 1 })}>Vyvron</span>
              <svg width="16" height="16" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginLeft: 3, marginBottom: 1 }}>
                <rect x="7" y="0" width="4" height="18" rx="1.5" fill={C.blue} />
                <rect x="0" y="7" width="18" height="4" rx="1.5" fill={C.blue} />
              </svg>
            </div>
            <p style={font(13, 400, C.white, { lineHeight: 1.7, maxWidth: 320 })}>
              Vyvron is a telehealth access and patient acquisition platform connecting eligible patients with independent licensed medical providers. Vyvron is not a medical provider or pharmacy.
            </p>
          </div>
          {[
            {
              title: "PROGRAM",
              links: [
                { label: "How It Works", scroll: "how-it-works" },
                { label: "Programs", to: "/programs" },
                { label: "Learn", to: "/learn" },
                { label: "FAQ", scroll: "faq" },
              ]
            },
            {
              title: "COMPANY",
              links: [
                { label: "About", to: "/about" },
                { label: "Contact", to: "/contact" },
              ]
            },
            {
              title: "LEGAL",
              links: [
                { label: "Privacy Policy", to: "/privacy" },
                { label: "Terms of Use", to: "/terms" },
                { label: "Telehealth Consent", href: "https://openloophealth.com/telehealth-consent", external: true },
                { label: "Notice of Privacy Practices", to: "/notice-of-privacy-practices" },
                { label: "HIPAA Privacy Policy", to: "/hipaa" },
                { label: "Accessibility Statement", to: "/accessibility" },
                { label: "Refund / Cancellation Policy", to: "/refund-policy" },
                { label: "Shipping Policy", to: "/shipping-policy" },
                { label: "Contact Us", to: "/contact" },
              ]
            },
          ].map((col, i) => (
            <div key={i}>
              <div style={font(12, 700, C.white, { letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: 18 })}>{col.title}</div>
              {col.links.map(link => (
                <div key={link.label} style={{ marginBottom: 10 }}>
                  {link.to ? (
                    <Link to={link.to} style={{ ...font(13, 400, C.white), textDecoration: "none", transition: "color 0.2s, text-decoration-color 0.2s" }}
                      onMouseEnter={e => { e.currentTarget.style.color = "#0A1628"; e.currentTarget.style.backgroundColor = "#FAF7F2"; e.currentTarget.style.textDecoration = "underline"; }}
                      onMouseLeave={e => { e.currentTarget.style.color = C.white; e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.textDecoration = "none"; }}>{link.label}</Link>
                  ) : link.scroll ? (
                    <span style={{ ...font(13, 400, C.white), cursor: "pointer", transition: "color 0.2s, text-decoration-color 0.2s" }}
                      onClick={() => document.getElementById(link.scroll)?.scrollIntoView({ behavior: "smooth" })}
                      onMouseEnter={e => { e.currentTarget.style.color = "#0A1628"; e.currentTarget.style.backgroundColor = "#FAF7F2"; e.currentTarget.style.textDecoration = "underline"; }}
                      onMouseLeave={e => { e.currentTarget.style.color = C.white; e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.textDecoration = "none"; }}>
                      {link.label}
                    </span>
                  ) : (
                    <a href={link.href} target={link.external ? "_blank" : undefined} rel={link.external ? "noopener noreferrer" : undefined} style={{ ...font(13, 400, C.white), textDecoration: "none" }}>{link.label}</a>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
        <div style={{
          background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)",
          borderRadius: 8, padding: "20px 24px", marginTop: 28,
        }}>
          <div style={font(11, 700, C.footerMuted, { letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 10 })}>
            Platform Disclaimer
          </div>
          <p className="vyv-disclosure-text vyv-disclosure-text--dark" style={font(13, 400, C.white, { lineHeight: 1.7 })}>
            Vyvron is a patient access platform, not a medical provider or pharmacy. Vyvron connects eligible patients with independent licensed medical providers for online evaluation and treatment options when clinically appropriate. Vyvron does not practice medicine, prescribe medication, or dispense drugs. All medical decisions are made by independent licensed providers.
          </p>
        </div>
        <div style={{
          background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)",
          borderLeft: `3px solid #237FD3`,
          borderRadius: 8, padding: "20px 24px", marginTop: 18,
        }}>
          <div style={font(11, 700, C.footerMuted, { letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 10 })}>
            Compounded Medication Disclosure
          </div>
          <p style={font(13, 400, C.white, { lineHeight: 1.7 })}>
            Some medications available through Vyvron's provider network may be compounded. Compounded medications are not FDA-approved and are not identical or equivalent to any FDA-approved branded medication.
          </p>
          <p className="vyv-disclosure-text vyv-disclosure-text--dark" style={font(12, 400, C.footerMuted, { lineHeight: 1.7, marginTop: 10 })}>
            Ozempic®, Wegovy®, Mounjaro®, and Zepbound® are registered trademarks of their respective manufacturers and are not affiliated with or endorsed by Vyvron. Information on this site is for general educational purposes only and is not medical advice.
          </p>
        </div>
        <div style={{
          background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)",
          borderLeft: `3px solid #237FD3`,
          borderRadius: 8, padding: "20px 24px", marginTop: 18,
        }}>
          <div style={font(11, 700, C.footerMuted, { letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 10 })}>
            State Availability
          </div>
          <p className="vyv-disclosure-text vyv-disclosure-text--dark" style={font(13, 400, C.white, { lineHeight: 1.7 })}>
            {(typeof window !== "undefined" && window.VYVRON_DISCLOSURES && window.VYVRON_DISCLOSURES.STATE_AVAILABILITY) || ""}
          </p>
        </div>
        <p className="vyv-disclosure-text vyv-disclosure-text--dark" style={font(11, 400, C.footerMuted, { marginTop: 18, lineHeight: 1.7, paddingTop: 18, borderTop: "1px solid rgba(255,255,255,0.10)" })}>
          Vyvron is not a medical provider, pharmacy, or insurance plan. Vyvron connects eligible patients with independent licensed medical providers for online evaluation and prescription treatment options when clinically appropriate. Medical decisions are made solely by independent licensed providers. Prescription treatment is not guaranteed. State, medication, pharmacy, and pricing availability may vary. Results are not guaranteed. © 2026 Vyvron. All rights reserved.
        </p>
        <p style={font(11, 400, C.footerMuted, { marginTop: 10, lineHeight: 1.7 })}>
          Vyvron LLC · 30 N Gould St, Ste N, Sheridan, WY 82801
        </p>
        <a href="https://www.legitscript.com/websites/?checker_keywords=vyvron.com"
           target="_blank" rel="noopener noreferrer"
           title="Verify LegitScript Approval for www.vyvron.com"
           style={{ display: "inline-block", marginTop: 14 }}>
          <img src="https://static.legitscript.com/seals/50305996.png"
               alt="Verify Approval for www.vyvron.com" width="73" height="79"
               loading="lazy" decoding="async" />
        </a>
      </div>
    </footer>
  );
}

function CookieBanner({ onAccept, onDecline }) {
  const bannerRef = useRef(null);
  useEffect(() => {
    const updateHeight = () => {
      document.documentElement.style.setProperty("--vy-cookie-height", `${bannerRef.current?.getBoundingClientRect().height || 0}px`);
    };
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(bannerRef.current);
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty("--vy-cookie-height");
    };
  }, []);
  return (
    <div ref={bannerRef} className="vyv-cookie-banner" style={{
      position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 999,
      background: C.white, borderTop: `1px solid ${C.border}`,
      padding: "16px 48px", display: "flex", alignItems: "center",
      justifyContent: "space-between", flexWrap: "wrap", gap: 16,
      boxShadow: "0 -4px 24px rgba(0,0,0,0.07)",
    }}>
      <p style={font(14, 400, "#374151", { maxWidth: 700, lineHeight: 1.6 })}>
        We use cookies to improve your experience and analyze site traffic. See our{" "}
        <Link to="/privacy-policy" style={{ color: C.navy, textDecoration: "underline", fontWeight: 600 }}>Privacy Policy</Link>.
      </p>
      <div style={{ display: "flex", gap: 12 }}>
        <button onClick={onDecline} style={{
          background: C.white, color: C.navy, border: `1.5px solid ${C.navy}`,
          borderRadius: 6, padding: "10px 22px",
          ...font(14, 500, C.navy, { letterSpacing: "0.5px" }),
          cursor: "pointer", transition: "background 0.2s, color 0.2s",
        }}
          onMouseEnter={e => { e.currentTarget.style.background = C.navy; e.currentTarget.style.color = C.white; }}
          onMouseLeave={e => { e.currentTarget.style.background = C.white; e.currentTarget.style.color = C.navy; }}>
          Decline
        </button>
        <button onClick={onAccept} style={{
          background: C.primaryBlue, color: C.white, border: "none",
          borderRadius: 6, padding: "11.5px 24px",
          ...font(14, 500, C.white, { letterSpacing: "0.5px" }),
          cursor: "pointer", transition: "background 0.2s",
        }}
          onMouseEnter={e => e.currentTarget.style.background = C.navy}
          onMouseLeave={e => e.currentTarget.style.background = C.primaryBlue}>
          Accept
        </button>
      </div>
    </div>
  );
}

function StickyBar({ visible, cookieVisible }) {
  const handleCTA = openIntake;
  return (
    <div className={`vyv-sticky-bar${visible ? " is-visible" : ""}${cookieVisible ? " vyv-sticky-with-cookie" : ""}`} style={{
      position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 998,
      transition: "transform 0.3s ease, opacity 0.3s ease",
      transform: visible ? "translateY(0)" : "translateY(110%)",
      opacity: visible ? 1 : 0,
      pointerEvents: visible ? "auto" : "none",
    }}>
      {/* Desktop sticky bar — brand text + CTA, full-width */}
      <div className="vyv-sticky-desktop" style={{
        background: C.white,
        borderTop: `1px solid ${C.border}`,
        boxShadow: "0 -8px 28px rgba(10,22,40,0.10)",
        padding: "14px 32px",
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 24,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
          <span style={serif(14, 700, C.navy, { letterSpacing: "0.2px" })}>Vyvron</span>
          <span style={font(13, 500, C.muted, { letterSpacing: "0.1px" })}>— Private Weight-Management Evaluation</span>
        </div>
        <button onClick={handleCTA} style={{
          background: C.primaryBlue, color: C.white, border: "none",
          borderRadius: 6, padding: "12px 26px",
          ...font(14, 500, C.white, { letterSpacing: "0.5px" }),
          cursor: "pointer", transition: "background 0.2s",
          minHeight: 44, whiteSpace: "nowrap",
        }}
          onMouseEnter={e => e.currentTarget.style.background = C.navy}
          onMouseLeave={e => e.currentTarget.style.background = C.primaryBlue}>
          See if you qualify
        </button>
      </div>
      {/* Mobile sticky bar — full-width navy CTA */}
      <div className="vyv-sticky-mobile" style={{
        display: "none",
        background: C.white,
        borderTop: `1px solid ${C.border}`,
        boxShadow: "0 -8px 28px rgba(10,22,40,0.12)",
        padding: "10px 14px calc(10px + env(safe-area-inset-bottom))",
      }}>
        <button onClick={handleCTA} style={{
          width: "100%",
          background: C.primaryBlue, color: C.white, border: "none",
          borderRadius: 6, padding: "14px 0",
          ...font(15, 600, C.white, { letterSpacing: "0.5px" }),
          cursor: "pointer", transition: "background 0.2s",
          minHeight: 48,
        }}
          onMouseEnter={e => e.currentTarget.style.background = C.navy}
          onMouseLeave={e => e.currentTarget.style.background = C.primaryBlue}>
          See if you qualify
        </button>
      </div>
    </div>
  );
}

export default function App() {
  const [scrolled, setScrolled] = useState(false);
  const [stickyVisible, setStickyVisible] = useState(false);
  const [cookieVisible, setCookieVisible] = useState(true);
  const [footerInView, setFooterInView] = useState(false);
  // Single app-level lead-modal controller: null | "default" | "exit".
  const [leadModal, setLeadModal] = useState(null);
  const leadModalRef = useRef(null);
  leadModalRef.current = leadModal;
  const footerRef = useRef(null);

  // Soft-capture lead flow: persist UTM/gclid/fbclid for the session (Part 4).
  useEffect(() => { captureLeadParams(); }, []);

  // Flag the open cookie banner on <html> so mobile CSS can reserve hero
  // space beneath it (keeps the third hero CTA tappable on first load).
  useEffect(() => {
    document.documentElement.classList.toggle("vy-cookie-open", cookieVisible);
    return () => document.documentElement.classList.remove("vy-cookie-open");
  }, [cookieVisible]);

  // Exit-intent (Part 5): desktop only, once per session, after 8s on page.
  useEffect(() => {
    if ("ontouchstart" in window) return; // desktop only
    if (sessionStorage.getItem("vy_exit_shown") === "true") return;
    let armed = false;
    const armTimer = setTimeout(() => { armed = true; }, 8000);
    const onMouseOut = (e) => {
      if (!armed) return;
      // Fires when the cursor leaves the viewport toward the top (tab bar / URL bar).
      if (e.relatedTarget || e.clientY > 0) return;
      if (sessionStorage.getItem("vy_exit_shown") === "true") return;
      if (leadModalRef.current) return; // a lead modal is already open
      sessionStorage.setItem("vy_exit_shown", "true");
      setLeadModal("exit");
    };
    document.addEventListener("mouseout", onMouseOut);
    return () => {
      clearTimeout(armTimer);
      document.removeEventListener("mouseout", onMouseOut);
    };
  }, []);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 12);
      setStickyVisible(window.scrollY > 400);
    };
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Hide sticky bar once the footer scrolls into view, so it never
  // covers legal/footer links.
  useEffect(() => {
    if (!footerRef.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => setFooterInView(entry.isIntersecting),
      { rootMargin: "0px 0px -10px 0px", threshold: 0 }
    );
    obs.observe(footerRef.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!("IntersectionObserver" in window)) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const sections = [...document.querySelectorAll("#main-content > section")];
    sections.forEach((section) => {
      if (section.getBoundingClientRect().top < window.innerHeight * 0.92) {
        section.classList.add("vyv-revealed");
      }
    });
    document.documentElement.classList.add("vyv-reveal-ready");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("vyv-revealed");
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
    );

    sections.forEach((section) => {
      if (!section.classList.contains("vyv-revealed")) observer.observe(section);
    });

    return () => {
      observer.disconnect();
      document.documentElement.classList.remove("vyv-reveal-ready");
      sections.forEach((section) => section.classList.remove("vyv-revealed"));
    };
  }, []);

  return (
    <div style={{ fontFamily: "'Inter', sans-serif", background: C.bg, color: C.text, overflowX: "clip" }}>
      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
      `}</style>
      {cookieVisible && <CookieBanner onAccept={() => setCookieVisible(false)} onDecline={() => setCookieVisible(false)} />}
      <StickyBar visible={stickyVisible && !cookieVisible && !footerInView} cookieVisible={cookieVisible && !footerInView} />
      <NavBar scrolled={scrolled} />
      <Ticker />
      <main id="main-content" className="vyv-home-main">
        <Hero />
        <NoGamesStrip />
        <TrustStrip />
        <SupportSection />
        <WhyChooseVyvron />
        <InspectUs />
        <HowItWorks />
        <OurPrograms />
        <Pricing />
        <TrustCredibility />
        <QualificationJourney />
        <LearnCallout />
        <FAQSection />
        <CaptureBand />
        <FinalCTA />
      </main>
      <div ref={footerRef}>
        <Footer />
      </div>
      {leadModal !== null && (
        <Suspense fallback={null}>
          <LeadCaptureModal
            open
            onClose={() => setLeadModal(null)}
            {...(leadModal === "exit"
              ? {
                  headline: "Before you go — see your options.",
                  subhead: "Takes 30 seconds. No commitment. Independent licensed providers.",
                }
              : {})}
          />
        </Suspense>
      )}
    </div>
  );
}
